/**
 * GroupEdit
 *
 * Per-group admin surface within a workspace. Mirrors the workspace's
 * Access tab pattern (tags + provisioned content) but constrains the
 * pickers to the parent workspace's content set so a group can't be
 * granted content the workspace itself doesn't have.
 *
 * Tabs:
 *   General  — name + description + seat limit (API-backed)
 *   Branding — per-group color + logo (groupsClient.updateGroupAccess)
 *   Access   — content tag rules + explicit course/path grants
 *              (groupsClient.updateGroupAccess; both filtered to the
 *              parent workspace's allowed content)
 *   Members  — add learners individually + bulk paste emails; list +
 *              remove (API-backed)
 */

import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Course,
  Group,
  Login,
  Tag,
  TagType,
  UpdateGroupAccessRequest,
  Workspace,
} from '../apis/api-client';
import {
  coursesClient,
  groupsClient,
  tagsClient,
  usersClient,
  workspacesClient,
} from '../apis/client-instances';
import { PageHeader } from '../components/common';
import GroupMembersTab from '../components/groups/GroupMembersTab';
import GroupBrandingTab from '../components/groups/GroupBrandingTab';
import GroupGeneralTab from '../components/groups/GroupGeneralTab';
import GroupAccessTab from '../components/groups/GroupAccessTab';
import {
  DashboardIcon,
  GroupIcon,
  LibraryBooksIcon,
  PersonIcon,
} from '../components/icons';
import {
  Box,
  CircularProgress,
  Tab,
  Tabs,
} from '../components/ui';
import { useLayoutConfig } from '../contexts/LayoutContext';
import { alert, getFileUrl, uploadFile } from '../hooks/function';

type GroupTab = 'general' | 'branding' | 'access' | 'members';


export default function GroupEditPage() {
  const { wsId, groupId } = useParams<{ wsId: string; groupId: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { setLayoutConfig } = useLayoutConfig();

  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [group, setGroup] = useState<Group | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<GroupTab>('general');

  // General
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // Seat limit — blank input means unlimited (matches the workspace
  // pattern). Persisted on the group's access bundle.
  const [seatLimitInput, setSeatLimitInput] = useState<string>('');
  const [generalSaving, setGeneralSaving] = useState(false);

  // Branding — per-group color + logo, persisted via updateGroupAccess.
  const [primaryColor, setPrimaryColor] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
  const [brandingSaving, setBrandingSaving] = useState(false);

  // Access — filtered to the *parent workspace's* allowed set.
  const [workspaceTagIds, setWorkspaceTagIds] = useState<Set<number>>(new Set());
  const [workspaceCourseIds, setWorkspaceCourseIds] = useState<Set<number>>(new Set());
  const [contentTags, setContentTags] = useState<Tag[]>([]);
  const [provisionedContent, setProvisionedContent] = useState<Course[]>([]);
  const [accessSaving, setAccessSaving] = useState(false);
  // Opt-in toggle. Default false → group inherits the parent
  // workspace's content set. When the admin flips it on, the custom
  // tag/content pickers below take effect (still constrained to a
  // subset of the workspace's grants).
  const [customizeContent, setCustomizeContent] = useState(false);

  // Members live in GroupMembersTab now; the page keeps only a live
  // count it reports up, for the General-tab seat-remaining caption.
  const [memberCount, setMemberCount] = useState(0);

  // Group admin (owner) — separate from the members list so the
  // identity of the admin is visible even when the Learners tab hasn't
  // been opened. Hydrated from the Group's ownerUserId on load.
  const [groupAdmin, setGroupAdmin] = useState<Login | null>(null);
  const [groupAdminSaving, setGroupAdminSaving] = useState(false);

  // Seat limit lookup — derived from saved group access; not stateful
  // on its own. Used both in the General-tab caption and as the gate
  // for new-member adds. Null = unlimited.
  const seatLimit = useMemo<number | null>(() => {
    const trimmed = seatLimitInput.trim();
    if (!trimmed) return null;
    const parsed = parseInt(trimmed, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  }, [seatLimitInput]);

  // Layout chrome — back to the parent workspace, no global catalog search.
  useEffect(() => {
    setLayoutConfig({
      showBackButton: true,
      backButtonText: 'Back to Workspace',
      backButtonAction: () => navigate(`/workspaces/${wsId}`),
      hideGlobalSearch: true,
    });
    return () => setLayoutConfig({ showBackButton: false, hideGlobalSearch: false });
  }, [wsId, navigate, setLayoutConfig]);

  // Initial load — workspace + group + workspace's access constraints +
  // group's saved access/branding.
  useEffect(() => {
    if (!wsId || !groupId) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const [ws, g] = await Promise.all([
          workspacesClient.getWorkspaceById(Number(wsId)),
          groupsClient.getGroupById(Number(groupId)),
        ]);
        if (cancelled) return;
        setWorkspace(ws);
        setGroup(g);
        setName(g.name || '');
        setDescription(g.description || '');

        // Hydrate the current group admin (owner) if set. We only need
        // enough to fill a Login-shaped object for the autocomplete to
        // display the chosen user — full enrichment happens on edit.
        if (g.ownerUserId) {
          try {
            const owner = await usersClient.getUserById(g.ownerUserId);
            if (!cancelled) setGroupAdmin(owner);
          } catch {
            if (!cancelled) {
              // Fall back to a stub shape so the chip still renders.
              setGroupAdmin({ id: g.ownerUserId, firstName: g.ownerName ?? undefined } as Login);
            }
          }
        } else {
          setGroupAdmin(null);
        }

        // Parent workspace's access set — used to constrain the group's
        // pickers so a group can't be granted content the workspace
        // itself doesn't have. Grant collections are enriched on the
        // single-workspace GET (getWorkspaceById), so they're present on
        // `ws` here. An empty set means the workspace owns everything it
        // can see (the pickers fall back to the full catalog below).
        setWorkspaceTagIds(new Set(ws.contentTagIds ?? []));
        setWorkspaceCourseIds(new Set(ws.provisionedCourseIds ?? []));

        // Group's saved access/branding — read off the loaded group
        // (getGroupById enriches contentTagIds/provisionedCourseIds).
        const groupTagIds = g.contentTagIds ?? [];
        const groupCourseIds = g.provisionedCourseIds ?? [];
        setSeatLimitInput(g.seatLimit != null ? String(g.seatLimit) : '');
        setCustomizeContent(g.customizeContent === true);
        setPrimaryColor(g.primaryColor || '');
        setLogoUrl(g.logoUrl || '');
        if (g.logoUrl) {
          try {
            const url = await getFileUrl(g.logoUrl);
            if (!cancelled) setLogoPreview(url || null);
          } catch {
            /* tolerate stale URLs */
          }
        }

        // Hydrate Tag + Course chips from saved IDs.
        const [tagRes, courseRes] = await Promise.all([
          groupTagIds.length > 0
            ? tagsClient
                .searchTags(undefined, TagType.CourseTag, undefined, true, false, groupTagIds, 1, 200)
                .then((r) => r.items ?? [])
                .catch(() => [] as Tag[])
            : Promise.resolve([] as Tag[]),
          groupCourseIds.length > 0
            ? Promise.all(
                groupCourseIds.map((cid) => coursesClient.getCourseById(cid).catch(() => null)),
              )
            : Promise.resolve([] as (Course | null)[]),
        ]);
        if (cancelled) return;
        setContentTags(tagRes as Tag[]);
        setProvisionedContent((courseRes as (Course | null)[]).filter((c): c is Course => !!c));
      } catch {
        if (!cancelled) {
          alert(enqueueSnackbar, 'Failed to load group', 'error');
          navigate(`/workspaces/${wsId}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsId, groupId]);

  // Parse the seat-limit input into a save-ready value or surface a
  // validation error. `false` means "don't save"; `null` means
  // unlimited; a number is the cap.
  const parseSeatLimit = (): number | null | false => {
    const trimmed = seatLimitInput.trim();
    if (!trimmed) return null;
    const parsed = parseInt(trimmed, 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
      alert(enqueueSnackbar, 'Seat limit must be a non-negative number, or empty for unlimited.', 'error');
      return false;
    }
    return parsed;
  };

  // Change the group admin. Calls the dedicated `changeGroupOwner`
  // endpoint so the backend can apply any side effects (cascading
  // permissions, etc.) rather than treating it as a generic update.
  const handleSaveGroupAdmin = async (next: Login | null) => {
    if (!groupId) return;
    setGroupAdminSaving(true);
    try {
      const updated = await groupsClient.changeGroupOwner(Number(groupId), {
        ownerUserId: next?.id,
      });
      setGroup(updated);
      setGroupAdmin(next);
      alert(
        enqueueSnackbar,
        next ? 'Group admin updated' : 'Group admin cleared',
        'success',
      );
    } catch (err: any) {
      alert(enqueueSnackbar, err?.response?.data || 'Failed to update group admin', 'error');
    } finally {
      setGroupAdminSaving(false);
    }
  };

  // Assemble the FULL group access/branding bundle from current tab
  // state. updateGroupAccess is a full replace, so every save (General
  // scalars, Branding, Access grants) sends the complete payload to
  // avoid one tab clobbering another's fields — same pattern as the
  // workspace access editor. seatLimit is passed in (the General tab
  // validates it before this point); logoUrl can be overridden when the
  // Branding tab has just uploaded a file whose URL isn't in state yet.
  const buildGroupAccessRequest = (
    seatLimit: number | null,
    logoOverride?: string,
  ): UpdateGroupAccessRequest => ({
    seatLimit: seatLimit ?? undefined,
    customizeContent,
    primaryColor: primaryColor || undefined,
    logoUrl: (logoOverride ?? logoUrl) || undefined,
    contentTagIds: contentTags.map((t) => t.id!).filter(Boolean),
    provisionedCourseIds: provisionedContent.map((c) => c.id!).filter(Boolean),
  });

  const handleSaveGeneral = async () => {
    if (!groupId) return;
    if (!name.trim()) {
      alert(enqueueSnackbar, 'Group name is required', 'error');
      return;
    }
    const seatLimit = parseSeatLimit();
    if (seatLimit === false) return;
    setGeneralSaving(true);
    try {
      await groupsClient.updateGroup(Number(groupId), {
        name: name.trim(),
        description: description || undefined,
      } as any);
      // Seat limit lives on the access bundle. The access endpoint runs
      // after updateGroup, so its return carries the just-written name +
      // the enriched grants — adopt it as the authoritative group.
      const updated = await groupsClient.updateGroupAccess(
        Number(groupId),
        buildGroupAccessRequest(seatLimit),
      );
      setGroup(updated);
      alert(enqueueSnackbar, 'Group updated', 'success');
    } catch (err: any) {
      alert(enqueueSnackbar, err?.response?.data || 'Failed to update group', 'error');
    } finally {
      setGeneralSaving(false);
    }
  };

  const handleSaveBranding = async () => {
    if (!groupId) return;
    if (primaryColor && !/^#[0-9A-Fa-f]{6}$/.test(primaryColor)) {
      alert(enqueueSnackbar, 'Primary color must be a valid hex color (e.g., #3ea6d6)', 'error');
      return;
    }
    setBrandingSaving(true);
    try {
      let finalLogoUrl = logoUrl;
      if (selectedLogo) {
        const result = await uploadFile(selectedLogo);
        finalLogoUrl = result?.uploadedUrl || '';
      }
      const updated = await groupsClient.updateGroupAccess(
        Number(groupId),
        buildGroupAccessRequest(seatLimit, finalLogoUrl),
      );
      setGroup(updated);
      setLogoUrl(finalLogoUrl);
      setSelectedLogo(null);
      alert(enqueueSnackbar, 'Branding saved', 'success');
    } catch (err: any) {
      alert(enqueueSnackbar, err?.response?.data || 'Failed to save branding', 'error');
    } finally {
      setBrandingSaving(false);
    }
  };

  const handleSaveAccess = async () => {
    if (!groupId) return;
    setAccessSaving(true);
    try {
      // When the admin flips OFF customize, we PRESERVE the saved
      // tag/content picks rather than blanking them — so flipping the
      // toggle back on restores their previous selection without
      // making them re-pick from scratch. The customizeContent flag is
      // the source of truth for which set is in effect.
      const updated = await groupsClient.updateGroupAccess(
        Number(groupId),
        buildGroupAccessRequest(seatLimit),
      );
      setGroup(updated);
      alert(enqueueSnackbar, 'Access settings saved', 'success');
    } catch (err: any) {
      alert(enqueueSnackbar, err?.response?.data || 'Failed to save access settings', 'error');
    } finally {
      setAccessSaving(false);
    }
  };

  // Constrain Access pickers to the parent workspace's allowed content.

  if (loading || !group) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <PageHeader
        icon={<GroupIcon />}
        title={group.name || 'Group'}
        description={`Group inside ${workspace?.name || 'workspace'}.`}
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={(_e, value) => setSelectedTab(value)}>
          <Tab label="General" value="general" />
          <Tab label="Branding" value="branding" icon={<DashboardIcon />} iconPosition="start" />
          <Tab label="Access" value="access" icon={<LibraryBooksIcon />} iconPosition="start" />
          <Tab label="Learners" value="members" icon={<PersonIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* ── General tab ───────────────────────────────────────────────── */}
      {selectedTab === 'general' && (
        <GroupGeneralTab
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
          groupAdmin={groupAdmin}
          groupAdminSaving={groupAdminSaving}
          onChangeGroupAdmin={handleSaveGroupAdmin}
          seatLimitInput={seatLimitInput}
          setSeatLimitInput={setSeatLimitInput}
          memberCount={memberCount}
          saving={generalSaving}
          onSave={handleSaveGeneral}
        />
      )}

      {/* ── Branding tab ─────────────────────────────────────────────── */}
      {selectedTab === 'branding' && (
        <GroupBrandingTab
          primaryColor={primaryColor}
          setPrimaryColor={setPrimaryColor}
          logoPreview={logoPreview}
          onLogoFileSelect={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              setSelectedLogo(file);
              setLogoPreview(URL.createObjectURL(file));
            }
          }}
          onLogoRemove={() => {
            setSelectedLogo(null);
            setLogoPreview(null);
            setLogoUrl('');
          }}
          saving={brandingSaving}
          onSave={handleSaveBranding}
        />
      )}

      {/* ── Access tab ───────────────────────────────────────────────── */}
      {selectedTab === 'access' && (
        <GroupAccessTab
          customizeContent={customizeContent}
          setCustomizeContent={setCustomizeContent}
          contentTags={contentTags}
          setContentTags={setContentTags}
          provisionedContent={provisionedContent}
          setProvisionedContent={setProvisionedContent}
          workspaceTagIds={workspaceTagIds}
          workspaceCourseIds={workspaceCourseIds}
          workspaceName={workspace?.name}
          saving={accessSaving}
          onSave={handleSaveAccess}
        />
      )}

      {/* ── Members tab ──────────────────────────────────────────────── */}
      {selectedTab === 'members' && (
        <GroupMembersTab
          groupId={Number(groupId)}
          seatLimit={seatLimit}
          ownerUserId={group?.ownerUserId}
          groupName={group?.name}
          onMemberCountChange={setMemberCount}
        />
      )}

    </Box>
  );
}
